"use client";

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { SESSION_STORAGE_KEYS } from "@/constants/session-storage-keys";
import { useInitializeForms } from "@/hooks/form/use-initialize-forms";
import { CachedLoanApplicationResponseBody } from "@/types/cached-loan-application";

type Application = {
	applicationId: string;
	cachedApplicationData?: CachedLoanApplicationResponseBody;
};

type ApplicationContextValue = Application & {
	reloadApplicationCache: () => Promise<void>;
};

export const ApplicationContext = createContext<ApplicationContextValue | null>(
	null
);
export const useApplication = () => {
	const ctx = useContext(ApplicationContext);
	if (!ctx)
		throw new Error(
			"useApplication must be used within ApplicationProvider"
		);
	return ctx;
};

/**
 * Like `useApplication`, but returns `null` instead of throwing when rendered
 * outside an `ApplicationProvider`. Used by shared views (e.g. the landing page)
 * that render both before an application exists (create) and within an
 * application flow (update).
 */
export const useOptionalApplication = () => useContext(ApplicationContext);

export const ApplicationProvider = ({
	value,
	children,
}: {
	value: Application;
	children: React.ReactNode;
}) => {
	const { applicationId, cachedApplicationData } = value;
	const [cachedData, setCachedData] = useState(cachedApplicationData);

	const reloadApplicationCache = useCallback(async () => {
		sessionStorage.removeItem(
			SESSION_STORAGE_KEYS.HAS_FETCHED_CACHE(applicationId)
		);

		const response = await fetch(
			`/api/db-operations/get-application/${applicationId}`,
			{ method: "GET" }
		);

		if (!response.ok) {
			throw new Error("Failed to reload application cache");
		}

		const data =
			(await response.json()) as CachedLoanApplicationResponseBody;
		setCachedData(data);
		sessionStorage.setItem(
			SESSION_STORAGE_KEYS.HAS_FETCHED_CACHE(applicationId),
			"true"
		);
	}, [applicationId]);

	useEffect(() => {
		if (!applicationId) return;
		// SSR already hydrated us with the application, so there is nothing to
		// fetch on the client. Skipping avoids a redundant round-trip and, more
		// importantly, avoids clobbering the SSR data on a full page reload.
		if (cachedApplicationData) return;

		const justCreated =
			sessionStorage.getItem(
				SESSION_STORAGE_KEYS.CREATED_APPLICATION_ID
			) === applicationId;

		if (justCreated) {
			// Only guards the render right after create-application, where
			// Goldfinger may not yet reflect the write. Consume it so a later
			// mount with no SSR data (e.g. returning from FTN) always retries.
			sessionStorage.removeItem(
				SESSION_STORAGE_KEYS.CREATED_APPLICATION_ID
			);
			return;
		}

		fetch(`/api/db-operations/get-application/${applicationId}`, {
			method: "GET",
		})
			.then((res) => {
				if (res.status === 404) return null;
				if (!res.ok)
					throw new Error("Failed to fetch application cache");
				return res.json();
			})
			.then((data) => {
				if (!data) return;
				setCachedData(
					data as unknown as CachedLoanApplicationResponseBody
				);
				sessionStorage.setItem(
					SESSION_STORAGE_KEYS.HAS_FETCHED_CACHE(applicationId),
					"true"
				);
			});
	}, [applicationId, cachedApplicationData]);

	const contextValue = useMemo(
		() => ({
			applicationId,
			cachedApplicationData: cachedData,
			reloadApplicationCache,
		}),
		[applicationId, cachedData, reloadApplicationCache]
	);

	useInitializeForms(cachedData);
	return (
		<ApplicationContext.Provider value={contextValue}>
			{children}
		</ApplicationContext.Provider>
	);
};
